import { useEffect, useState } from "react";
import { api, apiError, inr } from "@/lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";

const now = new Date();
const START = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const FYS = Array.from({ length: 6 }, (_, i) => `${START - i}-${String((START - i + 1) % 100).padStart(2, "0")}`);
const STATUS = {
  matched: ["Matched", "text-emerald-400"], mismatch: ["Mismatch", "text-rose-400"],
  not_prepared: ["Return not prepared", "text-amber-400"], no_data: ["No data", "text-muted-foreground"],
};
const tax = (t) => (t ? t.igst + t.cgst + t.sgst + t.cess : null);
const dsum = (d) => (d ? Object.values(d).reduce((a, b) => a + b, 0) : null);
const cell = (v) => (v == null ? "—" : inr(v));
const TH = "px-2 py-2 text-left";
const THR = "px-2 py-2 text-right";

function MonthTable({ months }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">Month-wise match: HSN summary vs GSTR-1 vs GSTR-3B</div>
      <table className="w-full text-[11px] font-mono" data-testid="hsn-month-table">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>Month</th><th className={THR}>HSN taxable</th><th className={THR}>HSN tax</th>
          <th className={THR}>GSTR-1 taxable</th><th className={THR}>GSTR-1 tax</th><th className={THR}>GSTR-3B taxable</th><th className={THR}>GSTR-3B tax</th>
          <th className={THR}>Diff vs GSTR-1</th><th className={THR}>Diff vs GSTR-3B</th><th className={TH}>Status</th>
        </tr></thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.period} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`hsn-month-${m.period}`}>
              <td className="whitespace-nowrap">{m.label}</td>
              <td className="text-right">{inr(m.books.taxable_value)}</td><td className="text-right">{inr(tax(m.books))}</td>
              <td className="text-right">{cell(m.gstr1?.taxable_value)}</td><td className="text-right">{cell(tax(m.gstr1))}</td>
              <td className="text-right">{cell(m.gstr3b?.taxable_value)}</td><td className="text-right">{cell(tax(m.gstr3b))}</td>
              <td className={`text-right ${Math.abs(dsum(m.diff_gstr1) || 0) > 1 ? "text-rose-400" : ""}`}>{cell(dsum(m.diff_gstr1))}</td>
              <td className={`text-right ${Math.abs(dsum(m.diff_gstr3b) || 0) > 1 ? "text-rose-400" : ""}`}>{cell(dsum(m.diff_gstr3b))}</td>
              <td className={`whitespace-nowrap ${STATUS[m.status][1]}`}>{STATUS[m.status][0]}{m.gstr1 && !m.gstr1_has_hsn ? " (totals only)" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {months.some((m) => m.gstr1 && !m.gstr1_has_hsn) && <p className="px-4 py-2 text-xs text-muted-foreground">"Totals only": this GSTR-1 was prepared before HSN tracking, so it is compared on totals.</p>}
    </div>
  );
}

function HsnTable({ rows }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">HSN-wise outward summary (Table 12) for the year</div>
      <table className="w-full text-[11px] font-mono" data-testid="hsn-outward-table">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>HSN</th><th className={TH}>Rate</th><th className={TH}>Docs</th><th className={THR}>Taxable</th>
          <th className={THR}>IGST</th><th className={THR}>CGST</th><th className={THR}>SGST</th><th className={THR}>Cess</th>
          <th className={THR}>In GSTR-1 (taxable)</th><th className={THR}>Taxable difference</th>
        </tr></thead>
        <tbody>
          {rows.map((h) => (
            <tr key={`${h.hsn}-${h.rate}`} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`hsn-row-${h.hsn}-${h.rate}`}>
              <td>{h.hsn}</td><td>{h.rate}%</td><td>{h.count}</td>
              {["taxable_value", "igst", "cgst", "sgst", "cess"].map((f) => <td key={f} className="text-right">{inr(h.books[f])}</td>)}
              <td className="text-right">{cell(h.gstr1?.taxable_value)}</td>
              <td className={`text-right ${Math.abs(h.diff?.taxable_value || 0) > 1 ? "text-amber-400" : ""}`}>{cell(h.diff?.taxable_value)}{h.partial ? " †" : ""}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={10} className="px-2 py-4 text-center text-muted-foreground font-sans">No sales in this year</td></tr>}
        </tbody>
      </table>
      {rows.some((h) => h.partial) && <p className="px-4 py-2 text-xs text-muted-foreground">† Compared only for months whose GSTR-1 includes the HSN summary.</p>}
    </div>
  );
}

export function HsnReportPanel({ clientId }) {
  const [fy, setFy] = useState(FYS[0]);
  const [rep, setRep] = useState(null);
  useEffect(() => {
    api.get(`/clients/${clientId}/reports/hsn?fy=${fy}`).then((r) => setRep(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId, fy]);
  const download = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/reports/hsn/export?fy=${fy}`, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename\*=UTF-8''([^;]+)/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = m ? decodeURIComponent(m[1]) : `${fy}_HSN_Summary.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="hsn-report-panel">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="hsn-fy-select" value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-md bg-card border border-border px-3 py-1.5 text-sm font-mono">
          {FYS.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        <button data-testid="hsn-download-button" onClick={download} className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-xs font-medium"><Download className="h-3.5 w-3.5" /> Download Excel</button>
        {rep && <span className="text-xs text-muted-foreground" data-testid="hsn-counts">{rep.counts.matched} month(s) matched · {rep.counts.mismatch} mismatch · {rep.counts.not_prepared} not prepared · {12 - rep.counts.matched - rep.counts.mismatch - rep.counts.not_prepared} no data</span>}
      </div>
      {rep && <><MonthTable months={rep.months} /><HsnTable rows={rep.hsn_outward} /></>}
    </div>
  );
}
